import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, Button as ThemeUiButton, useThemeUI } from "theme-ui";
import get from "lodash/fp/get";

import { CheckmarkIcon, ArrowLeftIcon, ArrowRightIcon } from "components/icons";
import { HorizontalScrollView } from "components";
import { getFormsHaveRequiredValues } from "utils";
import * as styles from "./Tabs.styles";

function PreviousButton(props) {
  return (
    <ThemeUiButton variant="unstyled" sx={styles.previousButton} {...props}>
      <ArrowLeftIcon />
    </ThemeUiButton>
  );
}

function NextButton(props) {
  return (
    <ThemeUiButton variant="unstyled" sx={styles.nextButton} {...props}>
      <ArrowRightIcon />
    </ThemeUiButton>
  );
}

function Tabs({
  tabs = [],
  selectedTabId,
  userChoiceValues = {},
  selectTab = () => {},
  UIVersion = 1,
}) {
  const {
    theme: { isDesktop },
  } = useThemeUI();

  const itemsRef = useRef([]);

  const selectedTabIndex = tabs.findIndex(({ id }) => id === selectedTabId);

  const customisationsVisited = useSelector(
    get(["app", "customisationsVisited"])
  );

  const fieldsTouched = useSelector(get(["app", "fieldsTouched"]));

  useEffect(() => {
    if (isDesktop) {
      const el = itemsRef.current[selectedTabIndex];
      const parentEl = el.parentElement.parentElement;
      parentEl.scroll({
        top: 0,
        left: el.offsetLeft + el.clientWidth / 2 - parentEl.clientWidth / 2,
        behavior: "smooth",
      });
    }
  }, [isDesktop, selectedTabIndex]);

  if (isDesktop || UIVersion === 2) {
    return (
      <Box sx={styles.desktopContainer} data-cy="tabs">
        <HorizontalScrollView
          sideGutter={20}
          itemGap={20}
          height={76}
          sideButtonWidth={76}
          leftButtonComponent={({ onClick, disabled }) => (
            <PreviousButton onClick={onClick} disabled={disabled} />
          )}
          rightButtonComponent={({ onClick, disabled }) => (
            <NextButton onClick={onClick} disabled={disabled} />
          )}
        >
          {tabs.map((customisation, index) => {
            const isSelected = selectedTabIndex === index;

            const formsHaveRequiredValues = getFormsHaveRequiredValues({
              customisation,
              customisationsVisited,
              userChoiceValues,
              fieldsTouched,
            });

            return (
              <ThemeUiButton
                key={customisation.id}
                variant="unstyled"
                onClick={() => selectTab(customisation.id)}
                sx={styles.tab({ isSelected, UIVersion })}
                ref={(el) => (itemsRef.current[index] = el)}
                data-cy="tab"
              >
                {formsHaveRequiredValues && UIVersion === 1 && (
                  <Box sx={styles.checkmark}>
                    <CheckmarkIcon />
                  </Box>
                )}
                <Box
                  sx={
                    UIVersion === 2
                      ? styles.tabTitleV2({ isSelected })
                      : styles.tabTitle
                  }
                >
                  {formsHaveRequiredValues && UIVersion === 2 && (
                    <CheckmarkIcon />
                  )}
                  {customisation.title}
                </Box>
              </ThemeUiButton>
            );
          })}
        </HorizontalScrollView>
      </Box>
    );
  }

  return (
    <Box sx={styles.tabs}>
      <PreviousButton
        onClick={() => selectTab(tabs[selectedTabIndex - 1]?.id)}
        disabled={selectedTabIndex === 0}
      />

      <Box sx={styles.mobileButtonContainer}>
        <ThemeUiButton variant="unstyled" sx={styles.tab({ isSelected: true })}>
          <Box sx={styles.tabTitle}>{tabs[selectedTabIndex].title}</Box>
        </ThemeUiButton>
      </Box>

      <NextButton
        onClick={() => selectTab(tabs[selectedTabIndex + 1]?.id)}
        disabled={selectedTabIndex + 1 >= tabs.length}
      />
    </Box>
  );
}

export default Tabs;
